import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { InAppTemplate } from "../../../shared/templates/inapp";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import { FiPlus } from "react-icons/fi";
import { BiSearch } from "react-icons/bi";
import BlogPostRow from "./components/BlogPostRow";
import {
  ConfirmationModal,
  ErrorModal,
  SuccessModal,
} from "../../../shared/components/modals";
import { BlogService } from "../../../services/blogService";
import {
  adaptBlogPostFromApi,
  adaptBlogPostToUpdateCommand,
  UIBlogPost,
} from "../../../services/blogAdapters";
import { BlogCategoryDto } from "../../../types/app/blogTypes";

import ReactPaginate from "react-paginate";
import { LoadingItem } from "../../../shared/components/loading";
import { AlertMsg } from "../../../shared/components/alert-msg";
import "./blog.scss";

type BlogPost = UIBlogPost;

const BlogManagement = (pageProps: any) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickEditId, setQuickEditId] = useState<string | null>(null);
  const [quickEditError, setQuickEditError] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [itemsPerPage] = useState<number>(6);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    loadBlogData(1); // Load first page on mount
    loadCategories(); // Load categories for filter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async () => {
    try {
      const data = await BlogService.getBlogCategories(false);
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {}
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category?.name || categoryId;
  };

  const loadBlogData = async (page: number) => {
    try {
      setLoading(true);
      setError(null);

      const response = await BlogService.getBlogPosts(page, itemsPerPage);

      const adaptedPosts = response.results.map((post: any) =>
        adaptBlogPostFromApi(post, "published")
      );

      setPosts(adaptedPosts);
      setCurrentPage(page - 1);
      setTotalPages(response.totalNumberOfPages);
      setTotalRecords(response.totalNumberOfRecords);
    } catch (err) {
      setError("Failed to load blog posts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (event: { selected: number }) => {
    const newPage = event.selected + 1;
    loadBlogData(newPage);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortPosts = (postsToSort: BlogPost[]) => {
    if (!sortColumn) return postsToSort;

    return [...postsToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "title":
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case "status":
          aValue = a.status || "published";
          bValue = b.status || "published";
          break;
        case "category":
          // Get first category ID for sorting (or empty string if none)
          aValue =
            a.categoryIds && a.categoryIds.length > 0 ? a.categoryIds[0] : "";
          bValue =
            b.categoryIds && b.categoryIds.length > 0 ? b.categoryIds[0] : "";
          break;
        case "author":
          aValue = a.author.name.toLowerCase();
          bValue = b.author.name.toLowerCase();
          break;
        case "date":
          aValue = new Date(a.publishDate);
          bValue = new Date(b.publishDate);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const renderSortableHeader = (
    column: string,
    label: string,
    className: string
  ) => (
    <th
      className={`${className} sortable-header`}
      onClick={() => handleSort(column)}
    >
      <div className="header-content">
        <span>{label}</span>
        <div className="sort-icons">
          {sortColumn === column ? (
            sortDirection === "asc" ? (
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon active"
              >
                <path
                  d="M1.86835 0.499999C2.25325 -0.166667 3.2155 -0.166667 3.6004 0.5L5.33245 3.5C5.71735 4.16667 5.23623 5 4.46643 5H1.00232C0.232523 5 -0.248601 4.16667 0.136299 3.5L1.86835 0.499999Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon active"
              >
                <path
                  d="M3.6004 4.5C3.2155 5.16667 2.25325 5.16667 1.86835 4.5L0.136298 1.5C-0.248602 0.833333 0.232524 0 1.00232 0L4.46643 0C5.23623 0 5.71735 0.833333 5.33245 1.5L3.6004 4.5Z"
                  fill="currentColor"
                />
              </svg>
            )
          ) : (
            <>
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon"
              >
                <path
                  d="M1.86835 0.499999C2.25325 -0.166667 3.2155 -0.166667 3.6004 0.5L5.33245 3.5C5.71735 4.16667 5.23623 5 4.46643 5H1.00232C0.232523 5 -0.248601 4.16667 0.136299 3.5L1.86835 0.499999Z"
                  fill="currentColor"
                />
              </svg>
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon"
              >
                <path
                  d="M3.6004 4.5C3.2155 5.16667 2.25325 5.16667 1.86835 4.5L0.136298 1.5C-0.248602 0.833333 0.232524 0 1.00232 0L4.46643 0C5.23623 0 5.71735 0.833333 5.33245 1.5L3.6004 4.5Z"
                  fill="currentColor"
                />
              </svg>
            </>
          )}
        </div>
      </div>
    </th>
  );

  const uniqueCategoryIds = Array.from(
    new Set(posts.flatMap((post) => post.categoryIds || []))
  ).sort();

  const filterPosts = (postsToFilter: BlogPost[]) => {
    return postsToFilter.filter((post) => {
      const matchesSearch =
        !searchTerm ||
        post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        !categoryFilter ||
        (post.categoryIds && post.categoryIds.includes(categoryFilter));

      return matchesSearch && matchesCategory;
    });
  };

  const allFilteredPosts = sortPosts(filterPosts(posts));
  const publishedFilteredPosts = sortPosts(
    filterPosts(posts.filter((post) => post.status === "published"))
  );
  const draftFilteredPosts = sortPosts(
    filterPosts(posts.filter((post) => post.status === "draft"))
  );
  const trashedFilteredPosts = sortPosts(
    filterPosts(posts.filter((post) => post.status === "trashed"))
  );

  // The API has already paginated `posts`, so render the current response page
  // directly. Slicing again with the server page offset makes page 2+ empty.
  const publishedPosts = publishedFilteredPosts;
  const draftPosts = draftFilteredPosts;
  const trashedPosts = trashedFilteredPosts;
  const sortedAllPosts = allFilteredPosts;

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(posts.map((post) => post.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectPost = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((postId) => postId !== id) : [...prev, id]
    );
  };

  const handleTrashPost = async (id: string) => {
    try {
      await BlogService.trashBlogPost(id);
      setPosts((prev) =>
        prev.map((post) =>
          post.id === id ? { ...post, status: "trashed" as const } : post
        )
      );
    } catch (error) {
      alert("Failed to move post to trash. Please try again.");
    }
  };

  const handleRestorePost = async (id: string) => {
    try {
      await BlogService.restoreBlogPost(id);
      setPosts((prev) =>
        prev.map((post) =>
          post.id === id ? { ...post, status: "draft" as const } : post
        )
      );
    } catch (error) {
      alert("Failed to restore post. Please try again.");
    }
  };

  const handleDeletePermanently = (id: string) => {
    setPostToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (postToDelete) {
      try {
        await BlogService.deleteBlogPost(postToDelete);

        await loadBlogData(currentPage + 1);

        setPostToDelete(null);
        setShowDeleteModal(false);

        setShowDeleteSuccess(true);
      } catch (error) {
        alert("Failed to delete post. Please try again.");
      }
    }
  };

  const handleQuickEdit = (id: string) => {
    setQuickEditId(quickEditId === id ? null : id);
  };

  const handleUpdatePost = async (updatedPost: Partial<BlogPost>) => {
    try {
      if (!updatedPost.id) return;

      const originalPost = posts.find((p) => p.id === updatedPost.id);
      if (!originalPost?.slug) {
        return;
      }

      const apiPost = await BlogService.getBlogPostBySlug(originalPost.slug);
      const fullPost = adaptBlogPostFromApi(apiPost);
      const completePost: UIBlogPost = {
        ...fullPost,
        ...updatedPost,
      } as UIBlogPost;

      const updateCommand = adaptBlogPostToUpdateCommand(completePost);

      await BlogService.updateBlogPost(updatedPost.id, updateCommand);

      setPosts((prev) =>
        prev.map((post) =>
          post.id === updatedPost.id ? { ...post, ...updatedPost } : post
        )
      );

      setQuickEditId(null);
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update post. Please try again.";
      setQuickEditError(errorMsg);
    }
  };

  return (
    <>
      <InAppTemplate
        pageHeadingTitle="Blog Management"
        childComponent={
          <div className="blog-management">
            <div className="blog-header">
              <h1>Posts</h1>
              <button
                className="btn-add-post"
                onClick={() => navigate("/app/blog/new")}
              >
                <FiPlus /> Add Post
              </button>
            </div>

            <div className="blog-tabs-container">
              <Tab.Container
                activeKey={activeTab}
                onSelect={(key) => {
                  setActiveTab(key || "all");
                  setCurrentPage(0); // Reset pagination when switching tabs
                }}
              >
                {/* Tabs and Search/Filter Row */}
                <div className="tabs-and-search">
                  <Nav variant="tabs" className="table_navs">
                    <Nav.Item>
                      <Nav.Link
                        eventKey="all"
                        data-active={activeTab === "all"}
                      >
                        <div className="table_nav_item">
                          All ({totalRecords})
                        </div>
                        <div className="underline"></div>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="published"
                        data-active={activeTab === "published"}
                      >
                        <div className="table_nav_item">
                          Published ({publishedFilteredPosts.length})
                        </div>
                        <div className="underline"></div>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="draft"
                        data-active={activeTab === "draft"}
                      >
                        <div className="table_nav_item">
                          Draft ({draftFilteredPosts.length})
                        </div>
                        <div className="underline"></div>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="trash"
                        data-active={activeTab === "trash"}
                      >
                        <div className="table_nav_item">
                          Trash ({trashedFilteredPosts.length})
                        </div>
                        <div className="underline"></div>
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  <div className="search-filter">
                    <div className="search-box">
                      <BiSearch size={20} color="#BDBDBD" />
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search blog"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="filter-btn-wrapper">
                      <select
                        className="filter-btn"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                      >
                        <option value="">Filter by</option>
                        {uniqueCategoryIds.map((categoryId: string) => (
                          <option key={categoryId} value={categoryId}>
                            {getCategoryName(categoryId)}
                          </option>
                        ))}
                      </select>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="black"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="4" y1="6" x2="20" y2="6"></line>
                        <circle
                          cx="12"
                          cy="6"
                          r="2"
                          fill="white"
                          stroke="black"
                        ></circle>
                        <line x1="4" y1="12" x2="20" y2="12"></line>
                        <circle
                          cx="8"
                          cy="12"
                          r="2"
                          fill="white"
                          stroke="black"
                        ></circle>
                        <line x1="4" y1="18" x2="20" y2="18"></line>
                        <circle
                          cx="16"
                          cy="18"
                          r="2"
                          fill="white"
                          stroke="black"
                        ></circle>
                      </svg>
                    </div>
                  </div>
                </div>

                <Tab.Content>
                  <Tab.Pane eventKey="all">
                    {loading ? (
                      <div className="loading-container">
                        <LoadingItem />
                      </div>
                    ) : error ? (
                      <div className="error-container">
                        <AlertMsg type="error" message={error} />
                        <button
                          onClick={() => loadBlogData(1)}
                          className="btn btn-primary"
                          style={{ marginTop: "1rem" }}
                        >
                          Retry
                        </button>
                      </div>
                    ) : sortedAllPosts.length === 0 ? (
                      <div className="empty-state">
                        <p>No posts found</p>
                        {(searchTerm ||
                          categoryFilter ||
                          statusFilter !== "all") && (
                          <button
                            onClick={() => {
                              setSearchTerm("");
                              setCategoryFilter("");
                              setStatusFilter("all");
                              loadBlogData(1);
                            }}
                            className="btn btn-link"
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="blog-table-container">
                          <table className="blog-table">
                            <thead>
                              <tr>
                                <th className="col-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={
                                      selectedIds.length ===
                                        sortedAllPosts.length &&
                                      sortedAllPosts.length > 0
                                    }
                                    onChange={handleSelectAll}
                                  />
                                </th>
                                {renderSortableHeader(
                                  "title",
                                  "Title",
                                  "col-title"
                                )}
                                {renderSortableHeader(
                                  "status",
                                  "Status",
                                  "col-status"
                                )}
                                {renderSortableHeader(
                                  "category",
                                  "Category",
                                  "col-category"
                                )}
                                {renderSortableHeader(
                                  "author",
                                  "Author",
                                  "col-author"
                                )}
                                {renderSortableHeader(
                                  "date",
                                  "Last Modified",
                                  "col-date"
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {sortedAllPosts.map((post) => (
                                <BlogPostRow
                                  key={post.id}
                                  post={post}
                                  isSelected={selectedIds.includes(post.id)}
                                  onSelect={handleSelectPost}
                                  onQuickEdit={handleQuickEdit}
                                  onUpdatePost={handleUpdatePost}
                                  onTrash={handleTrashPost}
                                  onRestore={handleRestorePost}
                                  onDeletePermanently={handleDeletePermanently}
                                  isQuickEditOpen={quickEditId === post.id}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {totalPages > 0 && (
                          <ReactPaginate
                            breakLabel="..."
                            disableInitialCallback
                            disabledClassName="disabled"
                            disabledLinkClassName="disabled-link"
                            nextLabel=">"
                            nextAriaLabel="Go to next page"
                            onPageChange={handlePageClick}
                            pageRangeDisplayed={4}
                            forcePage={currentPage}
                            className="pagination_items"
                            pageClassName="page_num"
                            pageLinkClassName="page_link"
                            activeClassName="active_page_link"
                            previousClassName="previous_page_link"
                            nextClassName="next_page_link"
                            pageCount={totalPages}
                            previousLabel="<"
                            previousAriaLabel="Go to previous page"
                            renderOnZeroPageCount={null}
                          />
                        )}
                      </>
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="published">
                    <div className="blog-table-container">
                      <table className="blog-table">
                        <thead>
                          <tr>
                            <th className="col-checkbox">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  selectedIds.length ===
                                    publishedPosts.length &&
                                  publishedPosts.length > 0
                                }
                              />
                            </th>
                            {renderSortableHeader(
                              "title",
                              "Title",
                              "col-title"
                            )}
                            {renderSortableHeader(
                              "status",
                              "Status",
                              "col-status"
                            )}
                            {renderSortableHeader(
                              "category",
                              "Category",
                              "col-category"
                            )}
                            {renderSortableHeader(
                              "author",
                              "Author",
                              "col-author"
                            )}
                            {renderSortableHeader(
                              "date",
                              "Last Modified",
                              "col-date"
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {publishedPosts.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="no-posts">
                                No published posts
                              </td>
                            </tr>
                          ) : (
                            publishedPosts.map((post) => (
                              <BlogPostRow
                                key={post.id}
                                post={post}
                                isSelected={selectedIds.includes(post.id)}
                                onSelect={handleSelectPost}
                                onQuickEdit={handleQuickEdit}
                                onUpdatePost={handleUpdatePost}
                                onTrash={handleTrashPost}
                                onRestore={handleRestorePost}
                                onDeletePermanently={handleDeletePermanently}
                                isQuickEditOpen={quickEditId === post.id}
                              />
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <ReactPaginate
                        breakLabel="..."
                        disableInitialCallback
                        disabledClassName="disabled"
                        disabledLinkClassName="disabled-link"
                        nextLabel=">"
                        nextAriaLabel="Go to next page"
                        onPageChange={handlePageClick}
                        pageRangeDisplayed={4}
                        forcePage={currentPage}
                        className="pagination_items"
                        pageClassName="page_num"
                        pageLinkClassName="page_link"
                        activeClassName="active_page_link"
                        previousClassName="previous_page_link"
                        nextClassName="next_page_link"
                        pageCount={totalPages}
                        previousLabel="<"
                        previousAriaLabel="Go to previous page"
                        renderOnZeroPageCount={null}
                      />
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="draft">
                    <div className="blog-table-container">
                      <table className="blog-table">
                        <thead>
                          <tr>
                            <th className="col-checkbox">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  selectedIds.length === draftPosts.length &&
                                  draftPosts.length > 0
                                }
                              />
                            </th>
                            {renderSortableHeader(
                              "title",
                              "Title",
                              "col-title"
                            )}
                            {renderSortableHeader(
                              "status",
                              "Status",
                              "col-status"
                            )}
                            {renderSortableHeader(
                              "category",
                              "Category",
                              "col-category"
                            )}
                            {renderSortableHeader(
                              "author",
                              "Author",
                              "col-author"
                            )}
                            {renderSortableHeader(
                              "date",
                              "Last Modified",
                              "col-date"
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {draftPosts.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="no-posts">
                                No draft posts
                              </td>
                            </tr>
                          ) : (
                            draftPosts.map((post) => (
                              <BlogPostRow
                                key={post.id}
                                post={post}
                                isSelected={selectedIds.includes(post.id)}
                                onSelect={handleSelectPost}
                                onQuickEdit={handleQuickEdit}
                                onUpdatePost={handleUpdatePost}
                                onTrash={handleTrashPost}
                                onRestore={handleRestorePost}
                                onDeletePermanently={handleDeletePermanently}
                                isQuickEditOpen={quickEditId === post.id}
                              />
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <ReactPaginate
                        breakLabel="..."
                        disableInitialCallback
                        disabledClassName="disabled"
                        disabledLinkClassName="disabled-link"
                        nextLabel=">"
                        nextAriaLabel="Go to next page"
                        onPageChange={handlePageClick}
                        pageRangeDisplayed={4}
                        forcePage={currentPage}
                        className="pagination_items"
                        pageClassName="page_num"
                        pageLinkClassName="page_link"
                        activeClassName="active_page_link"
                        previousClassName="previous_page_link"
                        nextClassName="next_page_link"
                        pageCount={totalPages}
                        previousLabel="<"
                        previousAriaLabel="Go to previous page"
                        renderOnZeroPageCount={null}
                      />
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="trash">
                    <div className="blog-table-container">
                      <table className="blog-table">
                        <thead>
                          <tr>
                            <th className="col-checkbox">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  selectedIds.length === trashedPosts.length &&
                                  trashedPosts.length > 0
                                }
                              />
                            </th>
                            {renderSortableHeader(
                              "title",
                              "Title",
                              "col-title"
                            )}
                            {renderSortableHeader(
                              "status",
                              "Status",
                              "col-status"
                            )}
                            {renderSortableHeader(
                              "category",
                              "Category",
                              "col-category"
                            )}
                            {renderSortableHeader(
                              "author",
                              "Author",
                              "col-author"
                            )}
                            {renderSortableHeader(
                              "date",
                              "Last Modified",
                              "col-date"
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {trashedPosts.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="no-posts">
                                No trashed posts
                              </td>
                            </tr>
                          ) : (
                            trashedPosts.map((post) => (
                              <BlogPostRow
                                key={post.id}
                                post={post}
                                isSelected={selectedIds.includes(post.id)}
                                onSelect={handleSelectPost}
                                onQuickEdit={handleQuickEdit}
                                onUpdatePost={handleUpdatePost}
                                onTrash={handleTrashPost}
                                onRestore={handleRestorePost}
                                onDeletePermanently={handleDeletePermanently}
                                isQuickEditOpen={quickEditId === post.id}
                              />
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <ReactPaginate
                        breakLabel="..."
                        disableInitialCallback
                        disabledClassName="disabled"
                        disabledLinkClassName="disabled-link"
                        nextLabel=">"
                        nextAriaLabel="Go to next page"
                        onPageChange={handlePageClick}
                        pageRangeDisplayed={4}
                        forcePage={currentPage}
                        className="pagination_items"
                        pageClassName="page_num"
                        pageLinkClassName="page_link"
                        activeClassName="active_page_link"
                        previousClassName="previous_page_link"
                        nextClassName="next_page_link"
                        pageCount={totalPages}
                        previousLabel="<"
                        previousAriaLabel="Go to previous page"
                        renderOnZeroPageCount={null}
                      />
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>
          </div>
        }
      />

      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title="Delete Post Permanently"
        message="Are you sure you want to permanently delete this post? This action cannot be undone."
        confirmText="Delete Permanently"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        variant="danger"
      />

      <ErrorModal
        show={!!quickEditError}
        onHide={() => setQuickEditError("")}
        title="Quick Edit Error"
        message={quickEditError}
      />

      <SuccessModal
        show={showDeleteSuccess}
        onHide={() => setShowDeleteSuccess(false)}
        title="Post Deleted"
        message="The blog post has been permanently deleted."
      />
    </>
  );
};

const mapStateToProps = (state: any) => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(BlogManagement);
